import { fetchData } from 'utils/fetch';
import { restAPIs } from 'utils/restAPIs';

// UI category ids -> API category ids.
const API_CATEGORY = { actors: 'actors', movies: 'films', directors: 'directors', mix: 'mix' };
export const QUESTION_COUNTS = [10, 20, 30];

export class QuizApiError extends Error {}

const pair = (en, ar) => ({ en: en ?? '', ar: ar ?? en ?? '' });

// The API returns the same round twice (en[] and ar[]) with identical ids,
// pictures and right answers; merge them so the UI can switch language mid-round.
const mergeQuestion = (en, ar = en) => {
  const options = (en.options || []).map((opt, i) => {
    const arOpt = (ar.options || [])[i] || opt;
    return {
      id: opt.id,
      text: pair(opt.text, arOpt.text),
      caption: pair(opt.caption, arOpt.caption),
      picture: opt.isPicAvailable ? opt.picture : null,
      isRight: opt.isRight === true,
    };
  });
  const right = options.find((opt) => opt.isRight);
  const isPictureChoice = en.optionType === 'picture';
  return {
    id: en.id,
    category: en.category,
    type: en.type,
    image: en.isPicAvailable ? en.picture : null,
    optionType: isPictureChoice ? 'picture' : 'text',
    prompt: pair(en.question, ar.question),
    options,
    correctKey: right?.id,
    // For picture choices the name lives in the caption.
    answer: right ? (isPictureChoice ? right.caption : right.text) : pair('', ''),
    answerPicture: right?.picture || null,
  };
};

const isValidQuestion = (q) =>
  q.correctKey &&
  q.options.length === 4 &&
  q.options.filter((opt) => opt.isRight).length === 1 &&
  new Set(q.options.map((opt) => opt.id)).size === 4;

export const fetchQuizRound = async ({ count, category, language }) => {
  const apiCategory = API_CATEGORY[category] || 'mix';
  const res = await fetchData(restAPIs.quizQuestions(count, apiCategory, language === 'ar' ? 'ar' : 'en'));

  // fetchData never throws: HTTP errors come back as { status, data }.
  if (res?.status && res?.data) {
    const detail = res.data.detail;
    throw new QuizApiError(typeof detail === 'string' ? detail : 'Could not load the quiz.');
  }
  // Generation failures arrive as HTTP 200 with an `error` field.
  if (!res || res.error || res.status_code !== 200 || !Array.isArray(res.en)) {
    throw new QuizApiError(res?.error || 'Could not load the quiz.');
  }

  const ar = Array.isArray(res.ar) ? res.ar : [];
  const round = res.en.map((q, i) => mergeQuestion(q, ar[i])).filter(isValidQuestion);
  if (!round.length) throw new QuizApiError('The quiz came back empty.');
  return round;
};
